/**
 * /teacher/lesson-plans — In-Person Cohort · Lesson Plan Look-Ahead
 *
 * Multi-day view of the daily Flight Plans for the in-person cohort
 * (Vance · Brandon II · Brave · Gracen · Mill). Shows:
 *   • School-year progress (Sept 9 → June 11)
 *   • Next N school days with per-grade CA standard + mini-lesson
 *   • Click any day → jump to /teacher-dashboard#planner for full edit
 *   • Founder button: "Warm Next 2 Weeks" (Claude pregeneration)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CalendarDays, ArrowLeft, Sparkles, RefreshCw, ChevronRight, GraduationCap, Users } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const daysBetween = (a, b) => Math.round((fromISO(b) - fromISO(a)) / 86400000);

const WINDOWS = [
  { key: '5',  label: 'This Week',   days: 5  },
  { key: '10', label: 'Next 2 Weeks', days: 10 },
  { key: '20', label: 'Next 4 Weeks', days: 20 },
];

// Grade groups shown for the in-person cohort. Group keys map to what the backend returns.
const COHORT_GROUPS = ['g5', 'g6', 'g7', 'g8'];
const GROUP_LABEL = { g4: 'Grade 4', g5: 'Grade 5', g6: 'Grade 6', g7: 'Grade 7', g8: 'Grade 8', g9: 'Grade 9' };
const GROUP_COLOR = {
  g5: 'border-sky-500/60 bg-sky-500/5 text-sky-200',
  g6: 'border-amber-500/60 bg-amber-500/5 text-amber-200',
  g7: 'border-emerald-500/60 bg-emerald-500/5 text-emerald-200',
  g8: 'border-fuchsia-500/60 bg-fuchsia-500/5 text-fuchsia-200',
};

const LessonPlanLookAhead = () => {
  const [windowKey, setWindowKey] = useState('10');
  const [fromDate, setFromDate] = useState(toISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // date -> boolean
  const [warming, setWarming] = useState(false);

  const days = useMemo(() => Number(WINDOWS.find((w) => w.key === windowKey)?.days || 10), [windowKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/academy/day-range?from_date=${fromDate}&days=${days}`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Teacher/founder login required — redirecting to sign-in.');
        setTimeout(() => { window.location.href = `/academy/login?next=${encodeURIComponent('/teacher/lesson-plans')}`; }, 800);
      } else {
        toast.error(e?.response?.data?.detail || 'Could not load lesson plans');
      }
    } finally { setLoading(false); }
  }, [fromDate, days]);
  useEffect(() => { load(); }, [load]);

  const warm = async () => {
    setWarming(true);
    try {
      const r = await api.post(`/academy/day-range/pregenerate?from_date=${fromDate}&days=${days}`, {}, { headers: authHeaders() });
      toast.success(r.data.message || 'Warming the cache — refresh in a couple minutes.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not queue pregeneration');
    } finally { setWarming(false); }
  };

  const cohort = data?.cohort || {};
  const totalYearDays = useMemo(() => {
    if (!cohort.start_date || !cohort.end_date) return 0;
    // Count Mon-Fri between start and end inclusive
    let count = 0;
    let cur = fromISO(cohort.start_date);
    const end = fromISO(cohort.end_date);
    while (cur <= end) { if (cur.getDay() >= 1 && cur.getDay() <= 5) count += 1; cur = new Date(cur.getTime() + 86400000); }
    return count;
  }, [cohort.start_date, cohort.end_date]);
  const dayNumber = useMemo(() => {
    if (!cohort.start_date) return 0;
    const today = toISO(new Date());
    if (today < cohort.start_date) return 0;
    let count = 0;
    let cur = fromISO(cohort.start_date);
    const end = fromISO(today);
    while (cur <= end) { if (cur.getDay() >= 1 && cur.getDay() <= 5) count += 1; cur = new Date(cur.getTime() + 86400000); }
    return count;
  }, [cohort.start_date]);
  const pct = totalYearDays ? Math.round((dayNumber / totalYearDays) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="lesson-plan-look-ahead">
      {/* Hero */}
      <div className="border-b-2 border-zinc-900 py-8 px-6" style={{ background: 'linear-gradient(to bottom, rgba(16,185,129,0.08), transparent)' }}>
        <div className="container mx-auto max-w-6xl">
          <Link to="/teacher-dashboard" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3 h-3" /> Flight Control
          </Link>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-emerald-400">In-Person Cohort · Lesson Plans</div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">
            Look <span className="text-emerald-400">Ahead</span>
          </h1>
          <div className="text-[11px] text-zinc-500 mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <span><CalendarDays className="w-3 h-3 inline mr-1" /> {cohort.start_date || '—'} → {cohort.end_date || '—'}</span>
            <span>· {totalYearDays} school days</span>
            {cohort.students?.length ? (
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {cohort.students.map((s) => `${s.name} (G${s.grade})`).join(' · ')}</span>
            ) : null}
          </div>
          {/* Year progress */}
          <div className="w-full h-1.5 bg-black mt-4 max-w-2xl">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mt-1">Day {dayNumber} of {totalYearDays} · {pct}% of year</div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-6 space-y-6">
        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => e.target.value && setFromDate(e.target.value)}
              className="bg-black border-2 border-zinc-800 text-zinc-200 text-xs font-mono px-3 py-2 outline-none focus:border-emerald-500"
              data-testid="range-from"
            />
          </div>
          <div className="flex gap-1">
            {WINDOWS.map((w) => (
              <button key={w.key} onClick={() => setWindowKey(w.key)}
                className={`text-[10px] uppercase tracking-widest font-black px-3 py-2 border-2 transition-colors ${windowKey === w.key ? 'bg-emerald-500 text-black border-emerald-500' : 'border-zinc-800 text-zinc-400 hover:border-emerald-500'}`}
                data-testid={`window-${w.key}`}>
                {w.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="ml-auto flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white px-3 py-2 transition-colors" data-testid="reload-btn">
            <RefreshCw className="w-3 h-3" /> Reload
          </button>
          <button onClick={warm} disabled={warming}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black border-2 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500 hover:text-black disabled:opacity-50 px-3 py-2 transition-colors"
            data-testid="warm-cache-btn">
            {warming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Warm Cache
          </button>
        </div>

        {/* Days */}
        {loading ? (
          <div className="border-2 border-zinc-900 px-6 py-10 flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
            <span className="text-xs text-zinc-500">Loading plans…</span>
          </div>
        ) : (
          <div className="space-y-3">
            {(data?.days || []).map((d) => (
              <DayCard
                key={d.date}
                d={d}
                expanded={!!expanded[d.date]}
                onToggle={() => setExpanded((s) => ({ ...s, [d.date]: !s[d.date] }))}
              />
            ))}
            {(data?.days || []).length === 0 && (
              <div className="text-center py-16 text-zinc-500 text-sm">No school days in this range.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DayCard = ({ d, expanded, onToggle }) => {
  const dObj = fromISO(d.date);
  const isToday = d.date === toISO(new Date());
  return (
    <div className={`border-2 ${isToday ? 'border-emerald-500' : d.cached ? 'border-zinc-800' : 'border-zinc-900'} bg-zinc-950 transition-colors`} data-testid={`day-${d.date}`}>
      <button onClick={onToggle} className="w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-zinc-900/40">
        <div className="min-w-[68px]">
          <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">{dObj.toLocaleDateString('en-US', { weekday: 'short' })}</div>
          <div className="text-2xl font-black text-white leading-none">{dObj.getDate()}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{dObj.toLocaleDateString('en-US', { month: 'short' })}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 ${isToday ? 'bg-emerald-500 text-black' : 'bg-zinc-900 text-emerald-400'}`}>
              {isToday ? 'Today' : d.weekday}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">{d.subject}</span>
            {!d.cached && (
              <span className="text-[9px] uppercase tracking-widest font-black text-amber-400 px-1.5 py-0.5 border border-amber-500/40">Draft · not generated yet</span>
            )}
          </div>
          {d.daily_theme ? (
            <div className="text-sm text-zinc-200 mt-1 leading-snug line-clamp-2">{d.daily_theme}</div>
          ) : (
            <div className="text-sm text-zinc-500 mt-1 italic">Anchors ready — click Warm Cache to expand mini-lessons</div>
          )}
          {/* Quick grade chips */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {COHORT_GROUPS.map((gk) => {
              const g = d.groups?.[gk] || {};
              return (
                <span key={gk} className={`text-[10px] font-black tracking-wide uppercase border px-2 py-1 ${GROUP_COLOR[gk] || 'border-zinc-800 text-zinc-400'}`}>
                  <span className="opacity-70 mr-1">{GROUP_LABEL[gk]}</span>
                  <span className="font-mono">{g.standard_code || '—'}</span>
                </span>
              );
            })}
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-zinc-600 mt-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t-2 border-zinc-900 px-5 py-4 space-y-3">
          {COHORT_GROUPS.map((gk) => {
            const g = d.groups?.[gk] || {};
            return (
              <div key={gk} className={`border p-3 ${GROUP_COLOR[gk] || 'border-zinc-800'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <GraduationCap className="w-3.5 h-3.5" />
                  <div className="text-[11px] font-black uppercase tracking-widest">{GROUP_LABEL[gk]}</div>
                  <div className="text-[10px] font-mono text-white/80">{g.standard_code || '—'}</div>
                  <div className="text-[10px] text-white/70">· {g.standard_text || ''}</div>
                </div>
                {g.mini_lesson ? (
                  <div className="text-[13px] text-white mt-2 leading-snug">{g.mini_lesson}</div>
                ) : (
                  <div className="text-[12px] text-white/60 mt-2 italic">Mini-lesson will be generated on-demand or via Warm Cache.</div>
                )}
                {(g.prompts || []).length > 0 && (
                  <ol className="mt-2 space-y-1 pl-5 list-decimal">
                    {g.prompts.map((p, i) => <li key={i} className="text-[12px] text-white/85 leading-snug">{p}</li>)}
                  </ol>
                )}
              </div>
            );
          })}
          <div className="pt-2">
            <Link
              to={`/teacher-dashboard#planner-${d.date}`}
              className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] border-2 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500 hover:text-black px-3 py-2 transition-colors"
              data-testid={`open-planner-${d.date}`}
            >
              Open in Day Planner (edit / worksheet) <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonPlanLookAhead;
